/**
 * DeliverableImportCard payload parsing and the UI -> agent feedback line
 * convention, both defined by proto/mono/ryugu/v1/deliverable_import.proto's
 * DeliverableImportCard message doc comment.
 */ const KIND_ORDER = [
    'overview',
    'workstream_intro',
    'proposal_review',
    'proposal_revised',
    'workstream_summary',
    'commit_result',
    'next_workstream',
    'paused_recap',
    'import_complete'
];
const KIND_ENUM_PREFIX = 'DELIVERABLE_IMPORT_CARD_KIND_';
function normalizeKind(raw) {
    if (typeof raw === 'number') {
        return KIND_ORDER[raw - 1] ?? null;
    }
    if (typeof raw === 'string') {
        if (!raw.startsWith(KIND_ENUM_PREFIX)) return null;
        const candidate = raw.slice(KIND_ENUM_PREFIX.length).toLowerCase();
        return KIND_ORDER.includes(candidate) ? candidate : null;
    }
    return null;
}
function str(v) {
    return typeof v === 'string' ? v : '';
}
/**
 * Parses a shanks tool-result output object for its `_deliverableImportCard`
 * wrapper (protojson of DeliverableImportCard). Returns null for any output
 * that isn't a deliverable-import card, so callers can fall through to
 * existing tool-output handling.
 *
 * `output` is usually already JSON.parsed by the stream adapter, but it falls
 * back to the raw string when parsing fails there — accept that string form
 * too, re-parsing it here, so a card still renders.
 */ export function parseDeliverableImportCard(output) {
    let value = output;
    if (typeof output === 'string') {
        try {
            value = JSON.parse(output);
        } catch  {
            return null;
        }
    }
    if (!value || typeof value !== 'object') return null;
    const card = value._deliverableImportCard;
    if (!card || typeof card !== 'object') return null;
    const c = card;
    const kind = normalizeKind(c.kind);
    if (!kind) return null;
    return {
        kind,
        programId: str(c.programId),
        changeSetId: str(c.changeSetId),
        workstreamKey: str(c.workstreamKey),
        proposalId: str(c.proposalId),
        nextWorkstreamKey: str(c.nextWorkstreamKey),
        message: str(c.message)
    };
}
/**
 * Formats the single-line UI -> agent feedback convention from the proto doc
 * comment:
 *   [deliverable-import] event=<e> change_set=<id> workstream=<key> proposal=<id> <free text>
 * An `x=` token is omitted entirely when its value is empty. Free text
 * newlines collapse to spaces so the whole thing stays one line.
 */ export function deliverableImportFeedback({ event, changeSetId, workstreamKey, proposalId, text }) {
    const tokens = [
        `event=${event}`
    ];
    if (changeSetId) tokens.push(`change_set=${changeSetId}`);
    if (workstreamKey) tokens.push(`workstream=${workstreamKey}`);
    if (proposalId) tokens.push(`proposal=${proposalId}`);
    const collapsedText = text.replace(/\s*\n+\s*/g, ' ').trim();
    return [
        '[deliverable-import]',
        ...tokens,
        collapsedText
    ].filter(Boolean).join(' ');
}
/** Display only the human-readable action; keep routing metadata in stored/model context. */ export function deliverableImportFeedbackText(text) {
    const match = text.match(/^\[deliverable-import\] event=(?:approved|rejected|deferred|revised|committed|navigate) (?:(?:change_set|workstream|proposal)=\S+ )*(.+)$/);
    if (!match) return text;
    return match[1].replace(/^Program Lead opened the Deliverables page filtered to "(.+)-"\.$/, 'Opened $1 deliverables.').replace(/^Program Lead opened the Deliverables page, unfiltered\.$/, 'Opened all deliverables.');
}
